<template>
  <div class="sms">
    <p class="intro">
      Sign up to SMS notifications and get up-to-the-minute back-in-stock
      alerts, sale updates and new collection access.
    </p>
    <form>
      <div class="input">
        <input
          v-model.trim="email"
          type="email"
          class="input__element"
          autocomplete="email"
          placeholder="E-mail address"
        />
        <div v-if="v$.email.$dirty" class="input__response">
          <ul>
            <li v-for="error of v$.email.$errors" :key="error.$uid">
              {{ error.$message }}
            </li>
          </ul>
        </div>
      </div>
      <div class="telephone-wrapper">
        <label class="field">
          <div class="input">
            <select
              class="input__element"
              :value="usersCountryCode"
              @change="handleCodeChange"
            >
              <option
                v-for="code in telephoneCodesOrdered"
                :key="code.code"
                :value="code.code"
              >
                {{ code.dial_code }}
              </option>
            </select>
            <div v-if="v$.selectedDialCode.$dirty" class="input__response">
              <ul>
                <li
                  v-for="error of v$.selectedDialCode.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </li>
              </ul>
            </div>
          </div>
        </label>

        <label class="field">
          <div class="input">
            <input
              v-model.trim="telephone"
              type="tel"
              class="input__element"
              autocomplete="tel"
              placeholder="Phone number"
            />
            <div v-if="v$.telephone.$dirty" class="input__response">
              <ul>
                <li v-for="error of v$.telephone.$errors" :key="error.$uid">
                  {{ error.$message }}
                </li>
              </ul>
            </div>
          </div>
        </label>
      </div>
      <div class="legal">
        By signing up via text, you agree to receive recurring automated
        marketing messages, including cart reminders, at the phone number
        provided. Consent is not a condition of purchase. Reply STOP to
        unsubscribe. Reply HELP for help.
      </div>

      <Button full-width @click="handleFormSubmit">Sign up to SMS</Button>
    </form>
    <template v-if="submitted">
      <div v-if="success" class="input form__response">
        <div class="input__response input__response--success">
          Success! You've signed up to SMS.
        </div>
      </div>
      <div v-if="!success" class="input form__response">
        <div class="input__response input__response--failure">
          Something went wrong, please try again later.
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { email, numeric, required } from "@vuelidate/validators";
import cloneDeep from "lodash-es/cloneDeep";

import telephoneCodes from "@/assets/json/telephone-codes.json";
import Button from "@/components/Button.vue";
import { getParams } from "@/helpers/url.js";
export default {
  name: "Sms",
  components: {
    Button,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: "",
      telephone: null,
      success: false,
      submitted: false,
      codes: telephoneCodes,
      selectedDialCode: null,
      usersCountryCode: null,
      klaviyoID: null,
    };
  },
  computed: {
    fullTelephoneNumber() {
      return this.selectedDialCode + this.telephone?.toString();
    },
    telephoneCodesOrdered() {
      const countryCodesToMoveToTop = ["GB", "US", "AU"];
      const codes = cloneDeep(telephoneCodes);

      return codes
        .sort((a, b) => a["dial_code"] - b["dial_code"])
        .sort((a, b) => {
          return countryCodesToMoveToTop.includes(a.code)
            ? -1
            : countryCodesToMoveToTop.includes(b.code)
            ? 1
            : 0;
        });
    },
  },
  created() {
    const { klaviyoID } = getParams(window.location);
    this.klaviyoID = klaviyoID;
  },
  mounted() {
    this.preSelectUsersTelephoneCode();
    if (this.klaviyoID) {
      this.fetchUsersEmail();
    }
  },
  methods: {
    handleCodeChange(event) {
      const value = event.target.value;
      this.usersCountryCode = value;

      const { dial_code } = this.codes.find((code) => {
        return code.code === value;
      });

      this.selectedDialCode = dial_code;
    },
    async preSelectUsersTelephoneCode() {
      const response = await fetch(
        "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
      );
      const { countryCode: usersCountryCode } = await response.json();

      const { dial_code } = this.codes.find(
        (code) => code.code === usersCountryCode
      );

      this.selectedDialCode = dial_code;
      this.usersCountryCode = usersCountryCode;
    },
    async handleFormSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        console.log("invalid", this.v$);
        return;
      }
      const updateProfileResponse = await fetch(
        `${process.env.RATANDBOA_NODE_APP_URL}/klaviyo/sms-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: this.email,
            phone_number: this.fullTelephoneNumber,
          }),
        }
      );
      this.submitted = true;
      console.log(updateProfileResponse);
      if (!updateProfileResponse.ok) {
        this.success = false;
        return;
      }
      this.success = true;
    },
    async fetchUsersEmail() {
      const response = await fetch(
        `${process.env.RATANDBOA_NODE_APP_URL}/klaviyo/fetch-email?id=${this.klaviyoID}`
      );
      const { email } = await response.json();
      if (email) {
        this.email = email;
      }
    },
  },
  validations() {
    return {
      email: { required, email },
      telephone: { required, numeric },
      selectedDialCode: { required },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.sms {
  padding: 60px 20px;
  margin: 60px auto 60px;
  max-width: 600px;
}

.intro {
  font-size: 14px;
  margin: 0 0 30px 0;
  text-align: center;
}
.legal {
  font-size: 11px;
  margin: 0 0 30px 0;
}
</style>
